import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import {
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const ContractorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    login_id: '',
    email: '',
    password: '',
    phone: '',
    company_name: '',
    specialties: '',
    admin_notes: ''
  });

  const [selectedSpecialties, setSelectedSpecialties] = useState([]);

  // 공사 유형 목록 가져오기
  const { data: settings } = useQuery(
    'contractor-form-settings',
    () => api.get('/admin/settings').then(res => res.data)
  );

  // 기존 소장 정보 가져오기 (수정 모드)
  const { data: contractor, isLoading } = useQuery(
    ['contractor', id],
    () => api.get(`/admin/contractors/${id}`).then(res => res.data),
    {
      enabled: isEdit,
      onSuccess: (data) => {
        setFormData({
          name: data.name || '',
          login_id: data.login_id || '',
          email: data.email || '',
          password: data.password || '',  // 현재 비밀번호 표시
          phone: data.phone || '',
          company_name: data.company_name || '',
          specialties: data.specialties || '',
          admin_notes: data.admin_notes || ''
        });
        if (data.specialties) {
          setSelectedSpecialties(data.specialties.split(',').map(s => s.trim()));
        }
      }
    }
  );

  // 공사 유형 목록
  const constructionTypes = React.useMemo(() => {
    if (!settings?.construction_types) return [];
    return settings.construction_types.split(',').map(type => type.trim());
  }, [settings]);

  // 전문 분야 토글
  const handleSpecialtyToggle = (type) => {
    setSelectedSpecialties(prev => {
      if (prev.includes(type)) {
        return prev.filter(s => s !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedSpecialties.length === constructionTypes.length) {
      setSelectedSpecialties([]);
    } else {
      setSelectedSpecialties([...constructionTypes]);
    }
  };

  // 소장 생성/수정 mutation
  const saveMutation = useMutation(
    (data) => {
      if (isEdit) {
        return api.put(`/admin/contractors/${id}`, data);
      } else {
        return api.post('/admin/contractors', data);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEdit ? '소장 정보가 수정되었습니다.' : '새 소장이 등록되었습니다.');
        navigate('/admin/contractors');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '저장에 실패했습니다.');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.login_id) {
      toast.error('소장명과 로그인 ID는 필수 입력 항목입니다.');
      return;
    }

    if (!isEdit && !formData.password) {
      toast.error('비밀번호를 입력해주세요.');
      return;
    }

    saveMutation.mutate({
      ...formData,
      specialties: selectedSpecialties.join(',')
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/contractors')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            소장 관리로 돌아가기
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? '소장 정보 수정' : '새 소장 등록'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEdit ? '소장의 정보를 수정합니다.' : '새로운 소장을 등록합니다.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 기본 정보 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-gray-400" />
              기본 정보
            </h2>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  소장명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  로그인 ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.login_id}
                  onChange={(e) => handleChange('login_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isEdit ? '현재 비밀번호' : '비밀번호'} {!isEdit && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    required={!isEdit}
                    placeholder={isEdit ? '수정하려면 새 비밀번호 입력' : '비밀번호 입력'}
                  />
                </div>
                {isEdit && (
                  <p className="mt-1 text-xs text-gray-500">
                    현재 설정된 비밀번호입니다. 수정하려면 새 비밀번호를 입력하세요.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  이메일
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="선택사항"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  연락처
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="010-0000-0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  업체명
                </label>
                <div className="relative">
                  <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 전문 분야 */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <ClipboardDocumentListIcon className="h-5 w-5 mr-2 text-gray-400" />
                전문 분야
              </h2>
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                {selectedSpecialties.length === constructionTypes.length ? '전체 해제' : '전체 선택'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {constructionTypes.map((type) => (
                <label
                  key={type}
                  className={`
                    flex items-center px-3 py-2 border rounded-lg cursor-pointer transition-colors
                    ${selectedSpecialties.includes(type) 
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700' 
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={selectedSpecialties.includes(type)}
                    onChange={() => handleSpecialtyToggle(type)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm">{type}</span>
                </label>
              ))}
            </div>
            
            {selectedSpecialties.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">선택된 전문 분야 ({selectedSpecialties.length}개):</span>{' '}
                  {selectedSpecialties.join(', ')}
                </p>
              </div>
            )}
          </div>

          {/* 관리자 메모 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-400" />
              관리자 메모
            </h2>
            
            <textarea
              value={formData.admin_notes}
              onChange={(e) => handleChange('admin_notes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="이 소장에 대한 내부 메모를 작성하세요. 소장 본인은 이 내용을 볼 수 없습니다."
            />
            <p className="mt-2 text-sm text-gray-500">
              * 이 메모는 관리자만 볼 수 있으며, 소장 본인에게는 표시되지 않습니다.
            </p>
          </div>

          {/* 버튼 */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/contractors')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saveMutation.isLoading ? '저장 중...' : (isEdit ? '수정' : '등록')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractorForm;